import { HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthSession, SignInResult } from '../models/auth-session.model';
import { SsoCodeToTokensResponse, SsoRefreshTokensResponse } from '../models/sso-code-to-tokens-response';

/** Marks an identity-provider request (e.g. token endpoint): `AuthInterceptor` passes it through untouched. */
export const AUTH_PROVIDER_REQUEST = new HttpContextToken<boolean>(() => false);

export const authProviderRequestContext = (): HttpContext => new HttpContext().set(AUTH_PROVIDER_REQUEST, true);

/**
 * Provider-neutral authentication contract, used as a DI token.
 *
 * Implementations hide the identity-provider SDK: no SDK type crosses this boundary and every operation is an
 * `Observable` that propagates provider errors (unless stated otherwise).
 */
export abstract class AuthProvider {
  /** One-time SDK configuration, invoked from an environment initializer. */
  abstract configure(): void;

  /** Native sign-in. Emits `NEW_PASSWORD_REQUIRED` when the user must set a new password. Errors on any other challenge. */
  abstract signIn(username: string, password: string): Observable<SignInResult>;

  /** Completes a `NEW_PASSWORD_REQUIRED` challenge started by `signIn`. */
  abstract completeNewPassword(newPassword: string): Observable<AuthSession>;

  /**
   * Current session. Errors when there is none; refreshes it first when the id token expires
   * within `minValidityMs`.
   */
  abstract getSession(options?: { minValidityMs?: number }): Observable<AuthSession>;

  /** Forces a token refresh and emits the refreshed session. */
  abstract refreshSession(): Observable<AuthSession>;

  /** Whether a non-expired session exists. Never errors. */
  abstract isValidSession(): Observable<boolean>;

  /** Restores a session from an existing refresh token. Never errors (`false` on failure). */
  abstract signInWithRefreshToken(refreshToken: string, userId: string): Observable<boolean>;

  abstract forgotPassword(username: string): Observable<void>;

  abstract confirmForgotPassword(params: { username: string; code: string; password: string }): Observable<void>;

  /** Local sign-out. */
  abstract signOut(): Observable<void>;

  /** Starts the SSO flow by redirecting the browser to the identity provider's authorization page. */
  abstract signInWithSso(): void;

  /** Exchanges the authorization `code` returned to the SSO callback for tokens. */
  abstract exchangeSsoCode(code: string): Observable<SsoCodeToTokensResponse>;

  /** Gets new SSO tokens from a refresh token. */
  abstract refreshSsoTokens(refreshToken: string): Observable<SsoRefreshTokensResponse>;
}
